/**
 * Shared Nova board state machine (platform-neutral, no JSX): the single source
 * for both {@link ./NovaBoardScreen} (native) and {@link ./NovaBoard.web}.
 *
 * Ported wholesale from the two former in-file implementations (they were
 * near-duplicates). Owns: board data + sequence-guarded load, the status filter,
 * lifecycle handlers (pause/resume/run-now/delete/template/approvals), the
 * builder open/edit state, and the three deep-link params (?runId=,
 * ?editAutomationId=, ?resumeToken=). Anything platform-flavored is injected
 * (`onOpenRun`) or lives in the platform component (delete confirm UI,
 * page-context publishing, panel-command subscription).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { NovaPendingApproval } from '@tabbio/types';
import { useHaptics } from '../../hooks/useHaptics';
import { useLocale } from '../../hooks/useLocale';
import { useToast } from '../../hooks/useToast';
import { resolveDeviceTimezone } from '../../lib/device-timezone';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { createUpgradeContext } from '../../lib/monetization-context';
import {
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../../lib/product-analytics';
import {
  consumePendingNovaDraft,
  type PendingNovaDraft,
} from '../../lib/pending-nova-draft';
import {
  automationRunIdOfTarget,
  clearPostAuthTarget,
  readPostAuthTarget,
} from '../../lib/auth-handoff';
import {
  approveRun,
  deleteAutomation,
  enableTemplate,
  getAutomationStats,
  listAutomations,
  listPendingApprovals,
  listTemplates,
  pauseAutomation,
  rejectRun,
  resumeAutomation,
  runNow,
  isNovaEntitlementDenied,
  isNovaRateLimited,
  isNovaActiveLimit,
  type NovaAutomationSummary,
  type NovaStats,
  type NovaTemplateSummary,
} from '../../lib/automation-api';
import {
  templateInputServerError,
  templateInputsPayload,
  templateNeedsInputs,
  type NovaTemplateInputKey,
  type NovaTemplateInputValues,
} from './nova-template-inputs';

export type BoardAutomation = NovaAutomationSummary & {
  nextRunAt?: string | Date | null;
};

export type NovaBoardFilter = 'all' | 'active' | 'paused';

export interface NovaBoardEditTarget {
  automationId: string;
  sentence: string;
}

interface UseNovaBoardStateOptions {
  /** Open the live run panel (web: dialog state; native: modal route push). */
  onOpenRun: (runId: string, name?: string) => void;
}

export function useNovaBoardState({ onOpenRun }: UseNovaBoardStateOptions) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const haptics = useHaptics();
  const upgradeModal = useUpgradeModal();
  const router = useRouter();
  const { locale } = useLocale();

  const [automations, setAutomations] = useState<BoardAutomation[]>([]);
  const [templates, setTemplates] = useState<NovaTemplateSummary[]>([]);
  const [approvals, setApprovals] = useState<NovaPendingApproval[]>([]);
  const [stats, setStats] = useState<NovaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resolvingRunId, setResolvingRunId] = useState<string | null>(null);
  const [busyTemplateKey, setBusyTemplateKey] = useState<string | null>(null);
  // A research template asks for its inputs (topic, link, PDF) first.
  const [templateForm, setTemplateForm] = useState<NovaTemplateSummary | null>(
    null,
  );
  const [templateFormBusy, setTemplateFormBusy] = useState(false);
  const [templateFormError, setTemplateFormError] = useState<{
    field: NovaTemplateInputKey;
    message: string;
  } | null>(null);
  const [filter, setFilter] = useState<NovaBoardFilter>('all');

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<NovaBoardEditTarget | null>(null);
  const [initialDraft, setInitialDraft] = useState<PendingNovaDraft | null>(
    null,
  );

  const requestRef = useRef(0);
  // Kept in a ref so the deep-link effect does not re-fire when the platform
  // component re-creates its onOpenRun callback.
  const onOpenRunRef = useRef(onOpenRun);
  onOpenRunRef.current = onOpenRun;

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const reqId = ++requestRef.current;
    if (options?.silent) setRefreshing(true);
    else setLoading(true);
    setLoadError(false);
    try {
      const [list, tmpl, appr, st] = await Promise.all([
        listAutomations(),
        listTemplates().catch(() => [] as NovaTemplateSummary[]),
        listPendingApprovals().catch(() => [] as NovaPendingApproval[]),
        getAutomationStats().catch(() => null),
      ]);
      if (reqId !== requestRef.current) return;
      setAutomations(list as BoardAutomation[]);
      setTemplates(tmpl);
      setApprovals(appr);
      setStats(st);
    } catch {
      if (reqId !== requestRef.current) return;
      setLoadError(true);
    } finally {
      if (reqId === requestRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const boardViewTrackedRef = useRef(false);
  useEffect(() => {
    if (loading || loadError || boardViewTrackedRef.current) return;
    boardViewTrackedRef.current = true;
    trackProductEvent('nova_board_viewed', {
      automationCount: automations.length,
      activeCount: automations.filter((item) => item.status === 'active')
        .length,
      draftCount: automations.filter((item) => item.status === 'draft').length,
      pendingApprovalCount: approvals.length,
      activeMax: stats?.activeMax ?? null,
    });
  }, [approvals.length, automations, loadError, loading, stats?.activeMax]);

  /** Pull-to-refresh / background reload: no skeleton flash. */
  const refresh = useCallback(() => load({ silent: true }), [load]);

  const filteredAutomations = useMemo(() => {
    if (filter === 'active') {
      return automations.filter((a) => a.status === 'active');
    }
    if (filter === 'paused') {
      return automations.filter((a) => a.status === 'paused');
    }
    return automations;
  }, [automations, filter]);

  // ── Deep links ────────────────────────────────────────────────────────────
  const params = useLocalSearchParams<{
    runId?: string;
    editAutomationId?: string;
    resumeToken?: string;
  }>();
  const deepLinkedRunRef = useRef<string | null>(null);
  const resumedDraftRef = useRef<string | null>(null);

  useEffect(() => {
    const runId = typeof params.runId === 'string' ? params.runId : null;
    if (runId && deepLinkedRunRef.current !== runId) {
      deepLinkedRunRef.current = runId;
      // A run link opened while signed out rode through login as a stored
      // post-auth target; it is fulfilled now, so '/' must not resume it again.
      if (automationRunIdOfTarget(readPostAuthTarget())) {
        clearPostAuthTarget();
      }
      // Drop the param so refreshing/navigating back doesn't re-open the panel.
      router.setParams({ runId: undefined });
      onOpenRunRef.current(runId);
    }
  }, [params.runId, router]);

  useEffect(() => {
    const token =
      typeof params.resumeToken === 'string' ? params.resumeToken : null;
    if (!token || resumedDraftRef.current === token) return;
    resumedDraftRef.current = token;
    void consumePendingNovaDraft(token).then((draft) => {
      router.setParams({ resumeToken: undefined });
      if (!draft) return;
      setEditing(null);
      setInitialDraft(draft);
      setBuilderOpen(true);
    });
  }, [params.resumeToken, router]);

  useEffect(() => {
    const automationId =
      typeof params.editAutomationId === 'string'
        ? params.editAutomationId
        : null;
    if (!automationId || automations.length === 0) return;
    const automation = automations.find((item) => item.id === automationId);
    if (!automation) return;
    setEditing({
      automationId,
      sentence: automation.description || automation.name,
    });
    setInitialDraft(null);
    setBuilderOpen(true);
    router.setParams({ editAutomationId: undefined });
  }, [automations, params.editAutomationId, router]);

  // ── Errors ────────────────────────────────────────────────────────────────
  // One graceful place for every Nova error: upgrade prompt, limit, rate-limit,
  // else a plain toast: never a dead-end.
  const handleNovaError = useCallback(
    (error: unknown, fallbackKey: string) => {
      if (isNovaEntitlementDenied(error)) {
        if (error.decision) {
          upgradeModal.open(
            createUpgradeContext({
              decision: error.decision,
              placement: fallbackKey.includes('run')
                ? 'nova_run'
                : 'nova_activate',
              entryPoint: `nova.board.${fallbackKey}`,
              returnTo: '/auto-apply',
            }),
          );
        } else {
          upgradeModal.open({
            placement: fallbackKey.includes('run')
              ? 'nova_run'
              : 'nova_activate',
            entryPoint: `nova.board.${fallbackKey}`,
            planSet: 'individual',
            recommendedTier: 'pro',
            returnTo: '/auto-apply',
          });
        }
        return;
      }
      if (isNovaActiveLimit(error)) {
        const max = error.meta?.max;
        // A plan that allows ZERO active automations is an upsell, not a
        // "pause one" situation: treat it like an entitlement denial.
        if (typeof max !== 'number' || max <= 0) {
          upgradeModal.open({
            placement: 'nova_activate',
            entryPoint: 'nova.board.active_limit',
            planSet: 'individual',
            recommendedTier: 'pro',
            featureKey: 'individual.automations',
            reason: 'wrong_tier',
            returnTo: '/auto-apply',
          });
          return;
        }
        toast.error(t('nova.limit.title'), t('nova.limit.body', { max }));
        return;
      }
      if (isNovaRateLimited(error)) {
        const scope = error.scope ?? 'automation';
        toast.error(
          t(`nova.rateLimit.${scope}.title`),
          t(`nova.rateLimit.${scope}.body`),
        );
        return;
      }
      toast.error(
        t(fallbackKey),
        error instanceof Error ? error.message : undefined,
      );
    },
    [t, toast, upgradeModal],
  );

  // ── Lifecycle handlers ────────────────────────────────────────────────────
  const handleToggle = useCallback(
    async (a: BoardAutomation) => {
      haptics.trigger('light');
      setBusyId(a.id);
      try {
        if (a.status === 'active') {
          await pauseAutomation(a.id);
          trackProductEvent('nova_automation_paused', { source: a.source });
          toast.success(t('nova.lifecycle.paused'));
        } else {
          await resumeAutomation(a.id);
          trackProductEvent('nova_automation_resumed', { source: a.source });
          toast.success(t('nova.lifecycle.resumed'));
        }
        await load();
      } catch (error) {
        handleNovaError(error, 'nova.lifecycle.failed');
      } finally {
        setBusyId(null);
      }
    },
    [t, toast, load, haptics, handleNovaError],
  );

  const handleRunNow = useCallback(
    async (a: BoardAutomation) => {
      haptics.trigger('light');
      setBusyId(a.id);
      try {
        // The run executes in the background: open the LIVE panel immediately
        // so the user watches steps + results land in real time.
        const run = await runNow(a.id);
        trackProductEvent('nova_run_started', {
          source: a.source,
          triggerType: 'manual',
        });
        onOpenRunRef.current(run.id, a.name);
      } catch (error) {
        handleNovaError(error, 'nova.lifecycle.runFailed');
      } finally {
        setBusyId(null);
      }
    },
    [haptics, handleNovaError],
  );

  /** Deletes without confirming: the platform component owns the confirm UI. */
  const performDelete = useCallback(
    async (a: BoardAutomation) => {
      setBusyId(a.id);
      try {
        await deleteAutomation(a.id);
        await load();
      } catch (error) {
        handleNovaError(error, 'nova.delete.failed');
      } finally {
        setBusyId(null);
      }
    },
    [load, handleNovaError],
  );

  const handleEnableTemplate = useCallback(
    async (tmpl: NovaTemplateSummary) => {
      haptics.trigger('light');
      if (templateNeedsInputs(tmpl)) {
        // The template card opens its small form; enabling waits for it.
        setTemplateFormError(null);
        setTemplateForm(tmpl);
        trackProductEvent('nova_template_inputs_opened', {
          templateKey: tmpl.key,
        });
        return;
      }
      setBusyTemplateKey(tmpl.key);
      try {
        await enableTemplate(tmpl.key);
        trackProductEvent('nova_template_draft_created', {
          templateKey: tmpl.key,
          templateStatus: tmpl.status,
        });
        toast.success(t('nova.templates.draftCreated'));
        await load();
      } catch (error) {
        trackProductEvent('nova_template_create_failed', {
          templateKey: tmpl.key,
          ...getErrorObservabilityProperties(error),
        });
        handleNovaError(error, 'nova.templates.enableFailed');
      } finally {
        setBusyTemplateKey(null);
      }
    },
    [t, toast, load, haptics, handleNovaError],
  );

  /** Enable the open template with the inputs its form collected. */
  const submitTemplateInputs = useCallback(
    async (values: NovaTemplateInputValues) => {
      const tmpl = templateForm;
      if (!tmpl || templateFormBusy) return;
      setTemplateFormBusy(true);
      setTemplateFormError(null);
      try {
        await enableTemplate(tmpl.key, {
          inputs: templateInputsPayload(tmpl.inputs ?? [], values),
          timezone: resolveDeviceTimezone(),
          locale,
        });
        trackProductEvent('nova_template_draft_created', {
          templateKey: tmpl.key,
          templateStatus: tmpl.status,
          withInputs: true,
        });
        haptics.trigger('success');
        toast.success(t('nova.templates.draftCreated'));
        setTemplateForm(null);
        await load();
      } catch (error) {
        const fieldError = templateInputServerError(error);
        if (fieldError) {
          setTemplateFormError(fieldError);
          return;
        }
        trackProductEvent('nova_template_create_failed', {
          templateKey: tmpl.key,
          ...getErrorObservabilityProperties(error),
        });
        handleNovaError(error, 'nova.templates.enableFailed');
      } finally {
        setTemplateFormBusy(false);
      }
    },
    [
      templateForm,
      templateFormBusy,
      locale,
      haptics,
      toast,
      t,
      load,
      handleNovaError,
    ],
  );

  const closeTemplateForm = useCallback(() => {
    if (templateFormBusy) return;
    setTemplateForm(null);
    setTemplateFormError(null);
  }, [templateFormBusy]);

  const handleResolve = useCallback(
    async (runId: string, approve: boolean) => {
      haptics.trigger('light');
      setResolvingRunId(runId);
      // Optimistic: drop the row immediately so it feels instant.
      setApprovals((cur) => cur.filter((a) => a.runId !== runId));
      try {
        if (approve) await approveRun(runId);
        else await rejectRun(runId);
        trackProductEvent('nova_approval_resolved', {
          decision: approve ? 'approved' : 'rejected',
        });
        toast.success(
          approve ? t('nova.runs.approved') : t('nova.runs.rejected'),
        );
        await load();
      } catch (error) {
        handleNovaError(error, 'nova.lifecycle.failed');
        await load(); // restore the row on failure
      } finally {
        setResolvingRunId(null);
      }
    },
    [t, toast, load, haptics, handleNovaError],
  );

  // ── Builder open/edit state ───────────────────────────────────────────────
  const openBuilderNew = useCallback(() => {
    haptics.trigger('light');
    trackProductEvent('nova_builder_opened', { mode: 'create' });
    setEditing(null);
    setInitialDraft(null);
    setBuilderOpen(true);
  }, [haptics]);

  const openBuilderEdit = useCallback(
    (a: BoardAutomation) => {
      haptics.trigger('light');
      trackProductEvent('nova_builder_opened', {
        mode: 'edit',
        automationStatus: a.status,
        source: a.source,
      });
      setEditing({
        automationId: a.id,
        sentence: a.description || a.name,
      });
      setInitialDraft(null);
      setBuilderOpen(true);
    },
    [haptics],
  );

  const closeBuilder = useCallback(() => {
    setBuilderOpen(false);
    setEditing(null);
    setInitialDraft(null);
    // Activation can stop at the subscription boundary while keeping the
    // server-side draft. Refresh so it is immediately resumable.
    void load();
  }, [load]);

  return {
    automations,
    filteredAutomations,
    templates,
    approvals,
    stats,
    loading,
    refreshing,
    loadError,
    busyId,
    resolvingRunId,
    busyTemplateKey,
    filter,
    setFilter,
    load,
    refresh,
    handleNovaError,
    handleToggle,
    handleRunNow,
    performDelete,
    handleEnableTemplate,
    templateForm,
    templateFormBusy,
    templateFormError,
    submitTemplateInputs,
    closeTemplateForm,
    handleResolve,
    builderOpen,
    editing,
    initialDraft,
    openBuilderNew,
    openBuilderEdit,
    closeBuilder,
  };
}
